import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../Toast";
import { uuid } from "../../lib/uuid";
import { Modal } from "../Modal";
import { lineStationsKey, saveLineStations, useLineState, useLineStations } from "../../lib/line";
import type { Job } from "../../lib/types";
import { formatMoney } from "../../lib/engage";
import { useUnits } from "../../hooks/useUnits";
import { fmtRate, fmtWeight, rateParts } from "../../lib/units";

// M6 wave-2 manager surface: per-job station editor + bottleneck view,
// opened from DashboardPage's Jobs tab ("Line" button on each row). One
// modal does both jobs — the bottleneck section only appears once stations
// actually exist (`hasStations`), sharing the SAME react-query cache entry
// (lineStationsKey) as the row's "Line" badge, so saving here updates the
// badge with no extra reload.
//
// Station-editor invariant ("exactly one is_final", enforced client-side per
// the brief): rather than validate at save time, the local row list just
// never allows the invariant to break — adding the first row auto-marks it
// final, and removing the current final row promotes the new last row.
// Reordering (up/down) doesn't touch is_final at all.

type EditRow = { key: string; name: string; isFinal: boolean };

function newRow(isFinal: boolean): EditRow {
  return { key: uuid(), name: "", isFinal };
}

export function LineStationsModal({ job, onClose }: { job: Job; onClose: () => void }) {
  const units = useUnits();
  const toast = useToast();
  const queryClient = useQueryClient();
  const stationsQuery = useLineStations(job.id);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Seed the editable list from the server ONCE per mount (not on every
  // background refetch, which would clobber in-progress edits) — mirrors
  // TargetSheet/JobsTab's own "load once, edit locally, save explicitly"
  // shape rather than a fully-controlled live-bound form.
  useEffect(() => {
    if (seeded || !stationsQuery.isFetched) return;
    setSeeded(true);
    const existing = stationsQuery.data || [];
    setRows(existing.length
      ? existing.map((s) => ({ key: String(s.id), name: s.name, isFinal: s.is_final }))
      : [newRow(true)]);
  }, [seeded, stationsQuery.isFetched, stationsQuery.data]);

  const hasStations = (stationsQuery.data?.length ?? 0) > 0;
  const stateQuery = useLineState(job.id, { enabled: hasStations, refetchInterval: hasStations ? 10_000 : false });

  function addRow() {
    setRows((r) => [...r, newRow(r.length === 0)]);
  }
  function removeRow(key: string) {
    setRows((r) => {
      const next = r.filter((x) => x.key !== key);
      if (next.length && !next.some((x) => x.isFinal)) next[next.length - 1] = { ...next[next.length - 1], isFinal: true };
      return next;
    });
  }
  function renameRow(key: string, name: string) {
    setRows((r) => r.map((x) => (x.key === key ? { ...x, name } : x)));
  }
  function setFinal(key: string) {
    setRows((r) => r.map((x) => ({ ...x, isFinal: x.key === key })));
  }
  function move(key: string, dir: -1 | 1) {
    setRows((r) => {
      const i = r.findIndex((x) => x.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= r.length) return r;
      const next = [...r];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    if (rows.some((r) => !r.name.trim())) {
      toast("Every station needs a name", "err");
      return;
    }
    setSaving(true);
    try {
      await saveLineStations(job.id, rows.map((r, i) => ({ name: r.name.trim(), position: i + 1, is_final: r.isFinal })));
      await queryClient.invalidateQueries({ queryKey: lineStationsKey(job.id) });
      toast("Stations saved", "ok");
    } catch {
      toast("Could not save stations — try again", "err");
    } finally {
      setSaving(false);
    }
  }

  const state = stateQuery.data;
  const maxGpm = state ? Math.max(1, ...state.stations.map((s) => s.station_gpm)) : 1;

  return (
    <Modal title={`Line stations — ${job.name}`} onClose={onClose}>
      <div className="small muted" style={{ marginBottom: 12 }}>
        Add each station product moves through, in order. Mark the last one final —
        that&rsquo;s where the whole batch gets its official weigh-out.
      </div>

      {rows.map((r, i) => (
        <div key={r.key} className="row" style={{ marginBottom: 8, gap: 6, flexWrap: "nowrap" }}>
          <input
            value={r.name}
            placeholder={`Station ${i + 1}`}
            maxLength={60}
            data-testid={`station-name-${i}`}
            onChange={(e) => renameRow(r.key, e.target.value)}
            style={{ minHeight: 44, flex: 1 }}
          />
          <label className="row" style={{ gap: 4, flex: "0 0 auto", whiteSpace: "nowrap" }} title="Final station — the batch's official weigh-out">
            <input type="radio" name="final-station" checked={r.isFinal} onChange={() => setFinal(r.key)} />
            <span className="small muted">Final</span>
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => move(r.key, -1)} disabled={i === 0} aria-label="Move up">↑</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => move(r.key, 1)} disabled={i === rows.length - 1} aria-label="Move down">↓</button>
          <button type="button" className="btn btn-ghost btn-sm" data-testid={`station-remove-${i}`} onClick={() => removeRow(r.key)} aria-label="Remove station">✕</button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" data-testid="station-add" onClick={addRow}>+ Add station</button>

      <div className="btn-row" style={{ marginTop: 14 }}>
        <button type="button" className="btn btn-ghost" data-testid="line-modal-close" onClick={onClose}>Close</button>
        <button type="button" className="btn btn-primary" data-testid="station-save" disabled={saving} onClick={save}>
          Save stations
        </button>
      </div>

      {hasStations && state && (
        <div className="line-bottleneck" data-testid="bottleneck-panel">
          <div className="stat-row">
            <div className="stat"><div className="stat-val">{units === "metric" ? state.line_rate_gpm : rateParts(state.line_rate_gpm, units).value}</div><div className="stat-lbl">{rateParts(state.line_rate_gpm, units).unit} line rate</div></div>
            <div className="stat"><div className="stat-val">{state.eta_min != null ? `${state.eta_min}m` : "—"}</div><div className="stat-lbl">ETA</div></div>
            <div className="stat"><div className="stat-val">{fmtWeight(state.best_hour_g, units)}</div><div className="stat-lbl">Best hour</div></div>
            <div className="stat"><div className="stat-val">{fmtWeight(state.current_hour_g, units)}</div><div className="stat-lbl">This hour</div></div>
          </div>
          {state.pool && (
            <div className="panel" data-testid="pool-panel">
              <div className="row" style={{ justifyContent: "space-between" }}>
                <b>Piece-rate pool</b>
                <b data-testid="pool-total">{formatMoney(state.pool.total_cents, state.pool.currency)}</b>
              </div>
              <div className="muted small" style={{ marginTop: 4 }}>
                {fmtWeight(state.job.total_grams, units)} at {formatMoney(state.pool.rate_cents_per_g, state.pool.currency)}/g, split by station-hours
              </div>
              {state.pool.shares.map((sh) => (
                <div key={sh.player_id} className="row small" style={{ justifyContent: "space-between", marginTop: 6 }} data-testid="pool-share">
                  <span>{sh.name} <span className="muted">· {sh.station_hours}h</span></span>
                  <span>{formatMoney(sh.cents, state.pool!.currency)}</span>
                </div>
              ))}
              {!state.pool.shares.length && <div className="muted small" style={{ marginTop: 6 }}>Nobody has clocked in yet.</div>}
            </div>
          )}
          {state.stations.map((s) => {
            const isBottleneck = s.id === state.bottleneck_station_id;
            return (
              <div
                key={s.id}
                className={`panel line-station-row${isBottleneck ? " bottleneck" : ""}`}
                data-testid="bottleneck-station-row"
              >
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <b>{s.name}{s.is_final ? " · weigh-out" : ""}</b>
                  {isBottleneck && (
                    <span className="pill bottleneck-marker" data-testid="bottleneck-marker">🔧 slow point</span>
                  )}
                </div>
                <div className="progress-track" style={{ marginTop: 8 }}>
                  <div className={`progress-fill${isBottleneck ? " bottleneck" : ""}`} style={{ width: `${Math.min(100, (s.station_gpm / maxGpm) * 100)}%` }} />
                </div>
                <div className="muted small" style={{ marginTop: 4 }}>
                  {units === "metric" ? `${s.station_gpm} g/min` : fmtRate(s.station_gpm, units)} · {s.workers.length ? s.workers.map((w) => w.name).join(", ") : "unstaffed"}
                </div>
                {isBottleneck && (
                  <div className="job-countdown" data-testid="feed-hint">Feed this station — it&rsquo;s setting the room&rsquo;s pace.</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
